import { html, TemplateResult, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { SvgUtility } from '~/ui/utils';
import { formatRichText } from '~/modules/utils/rich-text.helper';

import '~/ui/components/button';
import '~/ui/components/checkbox';
import '~/ui/components/input';
import '~/ui/components/module-title';
import '~/ui/components/radio-group';
import '~/ui/components/radio';
import '~/ui/components/textarea';
import '~/ui/components/phone-input';
import '~/ui/objects/module/body';
import '~/ui/objects/module/module';
import '~/ui/objects/module/optional';
import '~/ui/objects/module/title';
import './sales-sites';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-tailormade-offer-request.styles';
import { graphql } from '~/modules/graphql/generated';
import { Task } from '@lit/task';
import contentfulService from '~/modules/services/contentful-graphql-service';
import { Environment, Variant } from '~/modules/der-tailormade-offer-request/types';
import { LocaleCode } from '~/common/interfaces';
import { withOpportunities } from '~/modules/utils/opportunity-mixin';
import DERModuleElement from '~/modules/utils/der-module-element';

export class OfferRequestComponent extends withOpportunities(DERModuleElement) {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    // opportunity* properties inherited; override needed defaults:
    override opportunitySource = '948150003';
    override opportunityType = 'Offer';
    override opportunityOriginatingObjectTag = 'der-tailormade-offer-request';

    @property({ type: String, attribute: 'contact-language' })
    contactLanguage = '';

    @property({ type: Boolean, attribute: 'description-optional' })
    descriptionOptional = false;

    @property({ type: Boolean, attribute: 'first-name-optional' })
    firstNameOptional = false;

    @property({ type: String, attribute: 'form-name' })
    formName = 'offerForm';

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/branch-get-salessites';

    @property({ type: String, attribute: 'submit-url' })
    submitUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/offer-request-send-offer-data';

    @property({ type: String, attribute: 'variant' })
    variant: Variant = 'default';

    @property({ type: String, attribute: 'x-environment' })
    xEnvironment: Environment = 'production';

    @property({ type: Boolean, attribute: 'form-submitted-successfully' })
    formSubmittedSuccessfully = false;

    @state()
    protected isSubmitting: boolean = false;

    private emailPattern = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';
    private namePattern = "^(?!\\s+$)[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]+$";

    tRequestOffer = '';
    tIntro = '';
    tLabelText = '';
    tDataProtectionLink = '';
    tConfirmationTitle = '';
    tConfirmationText = '';
    tConfirmationClosing = '';
    tSubmitButton = '';
    tSalutation = '';
    tGenderFemale = '';
    tGenderMale = '';
    tGenderOther = '';
    tFirstName = '';
    tLastName = '';
    tPhone = '';
    tEMail = '';
    tBranch = '';
    tSelect = '';
    tOpportunityPax = '';
    tOpportunityTravelDate = '';
    tOpportunityBudget = '';
    tOpportunityCostConfirmation = '';
    tFormValidationErrorSpaces = '';
    tFormValidationErrorPhone = '';

    OfferFormQuery = graphql(`
        query OfferForm($locale: String!, $tenant: String!) {
            bcOfferRequestCollection(
                locale: $locale
                where: {
                    OR: [
                        { contentfulMetadata: { tags_exists: true, tags: { id_contains_all: [$tenant] } } }
                        { contentfulMetadata: { tags_exists: true, tags: { id_contains_all: ["tenant-kuoni"] } } }
                    ]
                }
                order: [sys_firstPublishedAt_DESC]
            ) {
                items {
                    requestOffer
                    intro
                    labelText
                    dataProtectionLink
                    confirmationTitle
                    confirmationText
                    confirmationClosing
                    submitButton
                    salutation
                    genderFemale
                    genderMale
                    genderOther
                    firstName
                    lastName
                    phone
                    eMail
                    branch
                    select
                    opportunityPax
                    opportunityTravelDate
                    opportunityBudget
                    opportunityCostConfirmation
                    formValidationErrorSpaces
                    formValidationErrorPhone
                }
            }
        }
    `);

    @state()
    private _contentTask = new Task(this, {
        /**
         * Fetches localized form copy from Contentful based on current language + tenant.
         * Runs automatically when args() change.
         * @returns Promise<void>
         * @throws Error when content fetch fails.
         */
        task: async () => {
            const response = await contentfulService.fetchGraphQL(this.OfferFormQuery, {
                locale: this.lang,
                tenant: `tenant-${this.tenant}`,
            });
            if (!response) throw new Error('Error fetching content');

            const item = response.bcOfferRequestCollection?.items[0] || {};

            Object.assign(this, {
                tRequestOffer: item.requestOffer || '',
                tLabelText: item.labelText || '',
                tIntro: item.intro || '',
                tDataProtectionLink: item.dataProtectionLink || '',
                tConfirmationTitle: item.confirmationTitle || '',
                tConfirmationText: item.confirmationText || '',
                tConfirmationClosing: item.confirmationClosing || '',
                tSubmitButton: item.submitButton || '',
                tSalutation: item.salutation || '',
                tGenderFemale: item.genderFemale || '',
                tGenderMale: item.genderMale || '',
                tGenderOther: item.genderOther || '',
                tFirstName: item.firstName || '',
                tLastName: item.lastName || '',
                tPhone: item.phone || '',
                tEMail: item.eMail || '',
                tBranch: item.branch || '',
                tSelect: item.select || '',
                tOpportunityPax: item.opportunityPax || '',
                tOpportunityTravelDate: item.opportunityTravelDate || '',
                tOpportunityBudget: item.opportunityBudget || '',
                tOpportunityCostConfirmation: item.opportunityCostConfirmation || '',
                tFormValidationErrorSpaces: item.formValidationErrorSpaces || '',
                tFormValidationErrorPhone: item.formValidationErrorPhone || '',
            });
        },
        args: () => [this.lang, this.tenant],
    });

    /**
     * Sends an analytics event upon form confirmation.
     * @param data
     */
    confirmationAnalyticsEvent(data: unknown) {
        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form-submitted',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    variant: this.variant,
                    action: 'confirmation view after form submit',
                    category: '',
                    tracking_data: this.additionalTrackingData,
                    data: data,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Form submit handler: validates data, sends analytics + POST request, then shows confirmation.
     */
    private onSubmitClick(e: SubmitEvent) {
        e.preventDefault();
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        try {
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);
            const formValues = Object.fromEntries(formData.entries());
            this._updateContactLanguage();
            this._updateOpportunitySourceUrl();

            const staticFormData = {
                formName: this.formName,
                variant: this.variant,
                firstNameOptional: this.firstNameOptional,
                descriptionOptional: this.descriptionOptional,
                ContactLanguage: this.contactLanguage,
                OpportunityDestination: this.opportunityDestination,
                OpportunityProductId: this.opportunityProductId,
                OpportunitySource: this.opportunitySource,
                OpportunitySourceURL: this.opportunitySourceUrl, // getter supplies location if unset
                OpportunityTitle: this.opportunityTitle,
                OpportunityType: this.opportunityType,
                OpportunityOwningTeam: this.opportunityOwningTeam,
                OpportunityProductBrand: this.opportunityProductBrand,
                OpportunityOriginatingObjectTag: this.opportunityOriginatingObjectTag,
            };

            const json = { ...staticFormData, ...formValues };

            const reducedAnalyticsdata = {
                productKey: this.opportunityProductId,
                name: this.opportunityTitle,
                email: formValues['ContactEmail'],
            };

            const baseValidation =
                !formData.get('ContactEmailConfirmation') &&
                formData.get('ContactEmail') &&
                (!this.firstNameOptional ? formData.get('ContactFirstName') : true) &&
                formData.get('ContactLastName') &&
                formData.get('ContactGender') &&
                formData.get('ContactPhone') &&
                (!this.descriptionOptional ? formData.get('OpportunityDescription') : true) &&
                (formData.get('OpportunityOwningTeam') || staticFormData.OpportunityOwningTeam !== '');

            const extendedValidation = formData.has('OpportunityPax')
                ? formData.get('OpportunityPax') &&
                  formData.get('OpportunityTravelDate') &&
                  formData.get('OpportunityBudget')
                : true;

            const costsValidation = formData.has('OpportunityCostConfirmation')
                ? formData.get('OpportunityCostConfirmation')
                : true;

            const isFormValid = baseValidation && extendedValidation && costsValidation;

            if (isFormValid) {
                const detail = {
                    name: 'form-submit',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    variant: this.variant,
                    action: 'form submit clicked',
                    category: 'form',
                    data: json,
                };
                /**
                 * Custom Event: der-analytics
                 * Purpose: Notify global analytics layer about a validated form submission.
                 * detail structure:
                 *   name            Module identifier ('form-submit')
                 *   element_name    Tag name of the component (lowercase)
                 *   element_id      DOM id if present
                 *   variant         Component variant
                 *   action          Interaction type ('confirmation view after form submit')
                 *   category        Reserved / unused (empty string)
                 *   data            Payload sent to backend (merged static + user form values)
                 */
                this.dispatchEvent(new CustomEvent('der-analytics', { detail: detail, bubbles: true, composed: true }));

                fetch(this.submitUrl, {
                    method: 'POST',
                    body: JSON.stringify(json),
                    headers: { 'Content-Type': 'application/json', 'x-environment': this.xEnvironment },
                })
                    .then((response) => {
                        if (response.ok) {
                            this.confirmationAnalyticsEvent(reducedAnalyticsdata);
                            this._showConfirmationPage();
                            this._scrollToConfirmation();
                        } else {
                            throw new Error(`HTTP-Fehler: ${response.status}`);
                        }
                    })
                    .catch((error: unknown) => {
                        console.log('Error in form submission:', error);
                        this.isSubmitting = false;
                    })
                    .finally(() => {
                        // Unlock after async completes
                        this.isSubmitting = false;
                    });
            } else {
                this._showConfirmationPage();
                this._scrollToConfirmation();
            }
        } catch (error) {
            console.error('Fehler bei der Formularübermittlung:', error);
            this.isSubmitting = false;
        }
    }

    /** Shows confirmation state after (attempted) submit. */
    private _showConfirmationPage() {
        /**
         * Custom Event: der.view-mode-change
         * Purpose: Inform parent/app shell that the component switched to confirmation mode.
         * detail:
         * mode: 'confirmation'  Signals UI state change after (attempted) submission.
         */
        this.dispatchEvent(
            new CustomEvent('der.view-mode-change', {
                detail: { mode: 'confirmation' },
                bubbles: true,
                composed: true,
            })
        );
        this.formSubmittedSuccessfully = true;
    }

    /** Derives CRM language code from locale if not already set. */
    private _updateContactLanguage() {
        const languageMap: { [key: string]: string } = {
            [LocaleCode.FR_CH]: '948150001',
            [LocaleCode.DE_CH]: '948150000',
        };
        this.contactLanguage = this.contactLanguage || languageMap[this.lang] || '';
    }

    /**
     * Sets opportunity source URL to current page if not already set.
     */
    _updateOpportunitySourceUrl() {
        this.opportunitySourceUrl = this.opportunitySourceUrl || (top || window)?.location?.href || '';
    }

    private _scrollToConfirmation() {
        setTimeout(() => {
            this.shadowRoot
                ?.querySelector('.m-offer-request__confirmation')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    }

    /** Decrements pax input (min 1). */
    private _minusButtonClick(e: Event) {
        e.preventDefault();
        const numberInput = this.shadowRoot?.querySelector(
            '.m-offer-request__number-input-wrapper__input'
        ) as HTMLInputElement;
        let value = parseInt(numberInput.value, 10);
        if (value > 1) {
            value--;
            numberInput.value = value + '';
        }
    }

    /** Increments pax input (max 100). */
    private _plusButtonClick(e: Event) {
        e.preventDefault();
        const numberInput = this.shadowRoot?.querySelector(
            '.m-offer-request__number-input-wrapper__input'
        ) as HTMLInputElement;
        let value = parseInt(numberInput.value, 10);
        if (value < 100) {
            value++;
            numberInput.value = value + '';
        }
    }

    /** Custom textarea whitespace validation. */
    private _handleInput(e: Event) {
        const customTextarea = e.target as HTMLTextAreaElement;
        const nativeTextarea = customTextarea.shadowRoot?.querySelector('textarea');
        if (nativeTextarea?.value.trim() === '') {
            nativeTextarea.setCustomValidity(this.tFormValidationErrorSpaces);
        } else {
            nativeTextarea?.setCustomValidity('');
        }
    }

    /** Renders sales team dropdown when owning team not preset. Internal helper. */
    private _renderTeamDropdown() {
        if (!this.opportunityOwningTeam) {
            return html` <div class="m-offer-request__input-wrapper">
                <der-tailormade-offer-request-sales-sites
                    name="OpportunityOwningTeam"
                    label="${this.tBranch}"
                    placeholder="${this.tSelect}"
                    sales-site-url="${this.salesSiteUrl}"
                    language="${this.lang}"
                    ?required="${true}"
                ></der-tailormade-offer-request-sales-sites>
            </div>`;
        }
        return html``;
    }

    /** Renders extended form fields (travel date, budget, pax) based on variant. Internal helper. */
    private _renderExtendedForm() {
        if (this.variant === 'extended' || this.variant === 'extended-costs') {
            return html`<div class="m-offer-request__input-wrapper">
                    <der-ui-input
                        label="${this.tOpportunityTravelDate}"
                        name="OpportunityTravelDate"
                        ?required="${true}"
                        type="text"
                    ></der-ui-input>
                </div>
                <div class="m-offer-request__input-wrapper m-offer-request__multiple-inputs-wrapper">
                    <der-ui-input
                        class="m-offer-request__budget-input"
                        label="${this.tOpportunityBudget}"
                        name="OpportunityBudget"
                        ?required="${true}"
                        type="number"
                        min="1"
                    ></der-ui-input>
                    <div class="m-offer-request__number-input-wrapper">
                        <div class="m-offer-request__number-input-wrapper__header">${this.tOpportunityPax}</div>
                        <div class="m-offer-request__number-input-wrapper__counter">
                            <button
                                class="m-offer-request__number-input-wrapper__number-control"
                                aria-label="Verringern"
                                @click="${this._minusButtonClick}"
                            >
                                <svg width="24" height="24" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                    ${SvgUtility['systemMinus']}
                                </svg>
                            </button>

                            <der-ui-input
                                class="m-offer-request__number-input-wrapper__input"
                                name="OpportunityPax"
                                ?required="${true}"
                                type="number"
                                value="2"
                                min="1"
                                max="100"
                            ></der-ui-input>

                            <button
                                class="m-offer-request__number-input-wrapper__number-control"
                                aria-label="Erhöhen"
                                @click="${this._plusButtonClick}"
                            >
                                <svg width="24" height="24" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                    ${SvgUtility['systemPlus']}
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>`;
        }
        return html``;
    }

    /** Renders cost confirmation checkbox for cost-related variants. Internal helper. */
    private _renderCostsConfirmation() {
        if (this.variant === 'costs' || this.variant === 'extended-costs') {
            return html`
                <div class="m-offer-request__checkbox-wrapper">
                    <der-ui-checkbox name="OpportunityCostConfirmation" ?required="${true}"> </der-ui-checkbox>
                    <div class="m-offer-request__checkbox-wrapper__text">${this.tOpportunityCostConfirmation}</div>
                </div>
            `;
        }
        return html``;
    }

    override render(): TemplateResult | undefined {
        return this._contentTask.render({
            /**
             * Task 'complete' callback: invoked after content loaded.
             * Renders either the input form (pre-submit) or confirmation (post-submit).
             */
            complete: () => html`
                ${!this.formSubmittedSuccessfully
                    ? html`
                          <!-- form view -->
                          <der-ui-module-wrapper>
                              <div class="m-offer-request">
                                  <der-ui-title-wrapper>
                                      <der-ui-module-title
                                          title="${this.tRequestOffer} &laquo;${this.opportunityTitle}&raquo;"
                                          intro="${this.tIntro}"
                                          module-name="m-offer"
                                      ></der-ui-module-title>
                                  </der-ui-title-wrapper>
                                  <der-ui-body-wrapper>
                                      <form name="${this.formName}" @submit="${this.onSubmitClick}">
                                          <div class="m-offer-request__form">
                                              <div class="m-offer-request__inputs-wrapper">
                                                  <div class="m-offer-request__input-wrapper">
                                                      <der-ui-radio-group
                                                          id="genderGroup"
                                                          name="ContactGender"
                                                          label="${this.tSalutation}"
                                                          language="${this.lang}"
                                                          ?required="${true}"
                                                      >
                                                          <der-ui-radio-button
                                                              value="2"
                                                              label="${this.tGenderFemale}"
                                                          ></der-ui-radio-button>
                                                          <der-ui-radio-button
                                                              value="1"
                                                              label="${this.tGenderMale}"
                                                          ></der-ui-radio-button>
                                                          <der-ui-radio-button
                                                              value="3"
                                                              label="${this.tGenderOther}"
                                                          ></der-ui-radio-button>
                                                      </der-ui-radio-group>
                                                  </div>
                                                  <div
                                                      class="m-offer-request__input-wrapper m-offer-request__multiple-inputs-wrapper"
                                                  >
                                                      <der-ui-input
                                                          label="${this.tFirstName}"
                                                          name="ContactFirstName"
                                                          ?required="${!this.firstNameOptional}"
                                                          pattern="${this.namePattern}"
                                                      ></der-ui-input>
                                                      <der-ui-input
                                                          label="${this.tLastName}"
                                                          name="ContactLastName"
                                                          ?required="${true}"
                                                          pattern="${this.namePattern}"
                                                      ></der-ui-input>
                                                  </div>
                                                  <div
                                                      class="m-offer-request__input-wrapper m-offer-request__multiple-inputs-wrapper"
                                                  >
                                                      <der-ui-phone-input
                                                          lang="${this.lang}"
                                                          label="${this.tPhone}"
                                                          name="ContactPhone"
                                                          input-placeholder=""
                                                          ?required="${true}"
                                                          validate-message="${this.tFormValidationErrorPhone}"
                                                      ></der-ui-phone-input>

                                                      <der-ui-input
                                                          label="${this.tEMail}"
                                                          name="ContactEmail"
                                                          inputmode="email"
                                                          pattern="${this.emailPattern}"
                                                          ?required="${true}"
                                                          type="email"
                                                      ></der-ui-input>
                                                  </div>
                                                  <div class="m-offer-request__email-confirmation">
                                                      <label for="emailConfirmation">eMail Confirmation</label>
                                                      <input
                                                          type="text"
                                                          id="emailConfirmation"
                                                          name="ContactEmailConfirmation"
                                                          tabindex="-1"
                                                          autocomplete="new-password"
                                                      />
                                                  </div>
                                                  ${this._renderTeamDropdown()}
                                                  <div class="m-offer-request__input-wrapper">
                                                      <der-ui-textarea
                                                          @input="${this._handleInput}"
                                                          label="${this.tLabelText}"
                                                          name="OpportunityDescription"
                                                          ?required="${!this.descriptionOptional}"
                                                          maxlength="3000"
                                                      ></der-ui-textarea>
                                                  </div>
                                                  ${this._renderExtendedForm()} ${this._renderCostsConfirmation()}
                                              </div>
                                              <div class="m-offer-request__actions">
                                                  <div class="m-offer-request__actions-button">
                                                      <der-ui-button
                                                          type="submit"
                                                          variant="primary"
                                                          ?disabled=${this.isSubmitting}
                                                      >
                                                          ${this.tSubmitButton}
                                                      </der-ui-button>
                                                  </div>
                                                  <div class="m-offer-request__actions-text">
                                                      ${unsafeHTML(this.tDataProtectionLink)}
                                                  </div>
                                              </div>
                                          </div>
                                      </form>
                                  </der-ui-body-wrapper>
                              </div>
                          </der-ui-module-wrapper>
                      `
                    : html`
                          <!-- confirmation view -->
                          <div class="m-offer-request__offer-request-item">
                              <div class="m-offer-request__confirmation">
                                  <i>
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                          ${SvgUtility['systemCheck']}
                                      </svg>
                                  </i>

                                  <div class="m-offer-request__confirmation-headline">${this.tConfirmationTitle}</div>
                                  <div class="m-offer-request__confirmation-body">
                                      <div class="m-offer-request__confirmation-text">
                                          ${unsafeHTML(formatRichText(this.tConfirmationText))}
                                      </div>
                                      <div class="m-offer-request__confirmation-closing">
                                          ${this.tConfirmationClosing}
                                      </div>
                                  </div>
                              </div>
                          </div>
                      `}
            `,
        });
    }
}
